<template>
  <div class="note-view-container">
    <div class="collectionnotes-top">
      <!-- TITLE -->
      <div class="note-title" @mousedown.self.prevent>
        <!-- prevents focussing on contenteditable span -->
        <div class="note-subheader">
          title
          <div class="float-right cursor-pointer" @click="$goToCollection({ id: currentCollection.id })">
            Collection: {{ currentCollection.name }}
          </div>
        </div>
        <div v-if="!editingTitle" class="note-title-text" @click="editTitle">
          {{ currentNote.title || "draft" }}
        </div>
        <input
          v-else
          type="text"
          class="title-input"
          ref="titleInput"
          spellcheck="false"
          @blur="updateTitle"
          @keydown.enter="e => e.target.blur()"
          :value="currentNote.title"
        />
      </div>
    </div>
    <div class="editor-container" ref="editorContainer" @click="handleInsideEditorClick">
      <editor-floating-menu
        :editor="editor"
        ref="editorMenu"
        class="floating-menu-container"
        v-slot="{ commands, isActive, menu }"
      >
        <div
          class="floating-menu"
          :class="{ 'is-active': menu.isActive && !menuHidden }"
          :style="`top: ${menu.top}px`"
        >
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.bold() }"
            @click.stop="commands.bold"
          >
            <i class="mdi mdi-format-bold mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.italic() }"
            @click.stop="commands.italic"
          >
            <i class="mdi mdi-format-italic mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.underline() }"
            @click.stop="commands.underline"
          >
            <i class="mdi mdi-format-underline mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click.stop="commands.bullet_list"
          >
            <i class="mdi mdi-format-list-bulleted mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click.stop="commands.ordered_list"
          >
            <i class="mdi mdi-format-list-numbered mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.heading({ level: 1 }) }"
            @click.stop="commands.heading({ level: 1 })"
          >
            <i class="mdi mdi-format-header-1 mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.heading({ level: 2 }) }"
            @click.stop="commands.heading({ level: 2 })"
          >
            <i class="mdi mdi-format-header-2 mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.heading({ level: 3 }) }"
            @click.stop="commands.heading({ level: 3 })"
          >
            <i class="mdi mdi-format-header-3 mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.blockquote() }"
            @click.stop="commands.blockquote()"
          >
            <i class="mdi mdi-format-quote-close mdi-18px" />
          </div>
          <div
            class="menu-item"
            :class="{ 'is-active': isActive.todo_list() }"
            @click.stop="commands.todo_list()"
          >
            <i class="mdi mdi-format-list-checks mdi-18px" />
          </div>
        </div>
      </editor-floating-menu>
      <editor-content
        v-scroll="storeScrollPosition"
        class="editor"
        :editor="editor"
        ref="editor"
        v-wheel="hideMenu"
        :spellcheck="spellCheckSetting"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { Editor, EditorContent, EditorFloatingMenu } from "tiptap";
import {
  Bold,
  Italic,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Heading,
  Blockquote,
  TodoItem,
  TodoList,
  History,
  Placeholder,
  Link
} from "tiptap-extensions";
const debounce = require("lodash/debounce");

export default {
  name: "NoteView",
  components: {
    EditorContent,
    EditorFloatingMenu
  },
  directives: {
    wheel: {
      inserted(el, binding) {
        // eslint-disable-line
        const f = function(evt) {
          // eslint-disable-line
          if (binding.value(evt, el)) {
            el.removeEventListener("wheel", f);
          }
        };
        el.addEventListener("wheel", f);
      }
    },
    // Scroll is used for storing scroll position. I guess 'wheel' would also work for that though. Scroll now just for consistency.
    scroll: {
      inserted(el, binding) {
        // eslint-disable-line
        const f = function(evt) {
          // eslint-disable-line
          if (binding.value(evt, el)) {
            el.removeEventListener("scroll", f);
          }
        };
        el.addEventListener("scroll", f);
      }
    }
  },
  data() {
    return {
      editingTitle: false,
      editor: new Editor({
        extensions: [
          new Bold(),
          new Italic(),
          new Underline(),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new Heading({ levels: [1, 2, 3] }),
          new Blockquote(),
          new TodoItem({ nested: true }),
          new TodoList(),
          new History(),
          new Placeholder({
            emptyNodeText: node => "Add your notes..."
          }),
          new Link()
        ],
        editable: true,
        onUpdate: debounce(({ getHTML }) => {
          const updateObj = {
            noteId: this.noteId,
            collectionId: this.currentNote.relatedCollectionId,
            notes: `${getHTML()}`,
            dateLastModified: this.$getCurrentUTC()
          };

          this.updateCollectionNotes(updateObj);
        }, 300),
        onTransaction: () => {
          this.menuHidden = false;
        },
        // disablePasteRules: true,
        // disableInputRules: true
        disablePasteRules: ['italic'],
      }),
      menuHidden: false,
      spellCheckSetting: false,
      unsubscribeVuex: null
    };
  },
  computed: {
    ...mapGetters({
      getNoteRef: "notes/getNoteRef"
    }),
    ...mapState({
      currentCollection: function(state) { // eslint-disable-line
        // The selectedCollection fallback is in case a card is added to a new collection that does not yet have an id, due to which currentCard still lacks a relatedCollectionId
        const fallback = { id: null, name: '-' };
        if (!this.currentNote) return fallback;

        const relatedCollectionId = this.currentNote.relatedCollectionId;
        if (!relatedCollectionId) return fallback;
        return state.collections.collectionNodes[relatedCollectionId] || state.collections.archivedCollectionNodes[relatedCollectionId] || fallback;
      },
    }),
    noteId() {
      return Number(this.$route.params.noteId);
    },
    currentNote() {
      return this.getNoteRef({ noteId: this.noteId });
    }
  },
  methods: {
    ...mapActions({
      updateCollectionNotes: "notes/updateCollectionNotes",
      v_storeScrollPosition: "appState/storeScrollPosition"
    }),
    ...mapMutations({
      AddOpenItem: "appState/AddOpenItem"
    }),
    storeScrollPosition: debounce(function(evt, el) {
      // eslint-disable-line
      // in CollectionView this.selectID is used to account for search and stuff. For notes atm not necessary.
      this.v_storeScrollPosition({
        type: "collectionNotes",
        id: this.noteId,
        scrollPosition: el.scrollTop
      });
    }, 50),
    editTitle(e) {
      const selObj = document.getSelection();
      let startPosition = Math.min(selObj.anchorOffset, selObj.focusOffset) - 1;
      let endPosition = Math.max(selObj.anchorOffset, selObj.focusOffset) - 1;

      if (startPosition === endPosition) {
        // No selected text
        startPosition = false;
        endPosition = false;
      }

      this.editingTitle = true;
      this.selectNoteTitle(startPosition, endPosition);
    },
    async selectNoteTitle(startPosition, endPosition) {
      await this.$nextTick();
      const inputField = this.$refs.titleInput;

      inputField.focus();
      const start = startPosition || 0;
      const end = endPosition || (this.currentNote.title && this.currentNote.title.length) || 0;
      inputField.setSelectionRange(start, end);
    },
    updateTitle(e) {
      this.editingTitle = false;
      if (this.currentNote.title === e.target.value) return;

      const now = this.$getCurrentUTC();
      this.updateCollectionNotes({
        noteId: this.currentNote.id,
        collectionId: this.currentNote.relatedCollectionId,
        title: e.target.value.trim() || "(no title)",
        dateLastModified: now
      });
    },
    handleInsideEditorClick() {
      const textIsSelected = !!window.getSelection().toString();
      if (textIsSelected) return;

      this.editor.setOptions({
        editable: true
      });
      this.editor.focus();
    },
    hideMenu() {
      this.menuHidden = true;
    }
  },
  async mounted() {
    console.log(this.currentNote);
    if (Object.keys(this.currentNote).length) {
      this.editor.setContent(this.currentNote.notes || "");
      this.AddOpenItem(this.currentNote);
    }

    const scrollToPosition = this.$store.state.appState.scrollState.collectionNotes[this.noteId];
    await this.$nextTick(); // Else editor has not loaded yet and cannot scroll. Seems to delay loading the notes a bit, though. Ah well.
    if (scrollToPosition) this.$refs.editor.$el.scrollTo(0, scrollToPosition);

    // Keep an eye on updates due to polling
    this.unsubscribeVuex = this.$store.subscribe(mutation => {
      if (mutation.type === "notes/SetNotes") {
        if (this.currentNote) {
          const cursorPosition = this.editor.view.state.selection.anchor;
          this.editor.setContent(this.currentNote.notes);
          // Dit is niet optimaal, want als er extra tekst bij is gekomen voor cursorPosition, verspringt de cursor.
          // Maar goed, is vooral bedoeld omdat hij af en toe denkt dat er iets gewijzigd is waardoor de cursor naar het einde verspringt, terwijl dat niet het
          // geval is. Dat is iig hiermee gefixt.
          if (cursorPosition) this.editor.setSelection(cursorPosition, cursorPosition);
          this.editor.focus();
        } else if (!this.currentNote && this.$route.name === "Note") {
          this.$goToCollection({ id: this.$store.state.collections.selectedCollection });
        }
      }
    });
  },
  beforeDestroy() {
    this.editor.destroy();
    this.unsubscribeVuex();
  },
  watch: {
    noteId() {
      if (this.noteId) {
        this.editor.setContent(this.currentNote.notes || "");
        this.AddOpenItem(this.currentNote);
        this.hideMenu();

        const scrollToPosition = this.$store.state.appState.scrollState.collectionNotes[
          this.noteId
        ];
        if (scrollToPosition) this.$refs.editor.$el.scrollTo(0, scrollToPosition);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~styles/colors";
@import "~styles/layouts";
@import "~styles/fonts";

.note-view-container {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  max-height: calc(
    100vh - 5.5rem
  ); // The height of the container when in notesEditor view (without topbar)
}

.collectionnotes-top {
  height: 5rem;
  flex-grow: 0;
  flex-shrink: 0;
  padding: 0 4rem;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
}
.note-subheader {
  font-size: 0.9rem;
  font-family: $quattrocentoSans;
  font-variant: small-caps;
  font-style: normal;
  color: $primary-dark;
}

// TITLE
.note-title {
  padding: 0.5rem 0 0;
  font-size: 1.5rem;
}
.note-title-text {
  cursor: text;
  border-radius: 0.25rem;
  padding: 0.15rem 0.25rem;
  margin-left: -0.25rem;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden; // shouldn't be such overflow anyways
}
.note-title-text:hover {
  background: rgba($primary, 0.5);
}
.title-input {
  outline: none;
  border: none;
  width: 100%;
  font-size: 1.5rem;
  line-height: 1.5rem;
  font-family: $bodyFont;
  color: $black;
  border-radius: 0.25rem;
  padding: 0.15rem 0.25rem;
  margin-left: -0.25rem;
}
.title-input:focus {
  background: rgba($primary, 0.5);
}

.editor-container {
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  height: 1px; // For flexbox
  flex-grow: 1;
  flex-shrink: 1;
}
.editor {
  position: relative;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 2rem 4rem 4rem;
  @include prosemirror-for-editor;

  ::v-deep .ProseMirror {
    min-height: calc(
      100vh - 10.5rem - 0.75rem
    ); // The height of the container when in notesEditor view (without topbar) [-0.75 is for scrollbar in firefox]
    padding: 2rem 1.5rem 0rem;
    overflow-y: hidden;
    overflow-x: hidden;
  }
}
// FLOATING MENU (EDITOR)
.floating-menu {
  position: absolute;
  z-index: 1;
  margin-top: -0.75rem;
  margin-left: 6rem;
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s;
}
.is-active {
  opacity: 1 !important;
  visibility: visible;
}
.menu-item {
  display: inline-block;
  height: 5vh;
  line-height: 5vh;
  padding: 0.25vw 0.5vw;
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s;
}
.menu-item:hover {
  opacity: 1;
}

// footer
.editor-footer {
  width: 100%;
  height: 3rem;
}
.float-right {
  float: right;
}
</style>
